import * as React from "react";
import * as crt from "./QuickJournalCrtText.css";
import * as chroma from "./QuickJournalChromaText.css";
import { TRenderable } from "source/app/presentation/Shared/SharedTypes";
import type { IQuickJournalEntry } from "../../Data/QuickJournalTypes";
import { latestJournalEntryFadeInDurationInMilliseconds } from "../../Data/QuickJournalTypes";
import { GetQuickJournalTheme, type QuickJournalTheme } from "../QuickJournalTheme";

export function ShowRecentJournalEntry({
    entry,
    opacity,
    hideTimeStamp,
    shouldFadeIn = false,
    isNewSubmission = false,
    distanceFromNewest = 0,
    fadeInDelayInMilliseconds = 0,
    theme,
}: {
    entry: IQuickJournalEntry | TRenderable | null;
    opacity: number;
    hideTimeStamp: boolean;
    shouldFadeIn?: boolean;
    isNewSubmission?: boolean;
    distanceFromNewest?: number;
    fadeInDelayInMilliseconds?: number;
    theme?: QuickJournalTheme;
}) {
    const quickJournalTheme = GetQuickJournalTheme(theme);
    const wasSubmittedOnMount = React.useRef(isNewSubmission).current;

    if (!entry) {
        return <div style={recentJournalEntryRowStyle}>&nbsp;</div>;
    }

    const isJournalEntry =
        typeof entry === "object" && "text" in entry && "timeStamp" in entry;
    const text = isJournalEntry ? entry.text : entry;
    const timeStamp = isJournalEntry ? entry.timeStamp : "";
    const phosphorColor = ["#c6cdf7", "#cbbff7", "#cbbcf7", "#ddadf4", "#ecd0fa"][
        Math.min(4, Math.max(0, distanceFromNewest))
    ];
    const isCyberpunk = quickJournalTheme.id === "cyberpunk";
    const isCyberpunkSubmission = isCyberpunk && wasSubmittedOnMount;
    const shouldAnimateRow = shouldFadeIn || isCyberpunkSubmission;
    const fadeDuration = isCyberpunkSubmission
        ? "var(--quickJournalCrtSettleDuration)"
        : `${latestJournalEntryFadeInDurationInMilliseconds}ms`;
    const fadeEasing = isCyberpunkSubmission ? "ease-in-out" : "ease-out";
    const rowStyle = {
        ...recentJournalEntryRowStyle,
        opacity,
        ...(isCyberpunk ? {
            color: phosphorColor,
            "--quickJournalHistoryColor": phosphorColor,
            "--quickJournalCrtSettleDuration": "650ms",
        } : {}),
    } as React.CSSProperties;

    return (
        <div
            className={isCyberpunk ? `${crt.row} ${wasSubmittedOnMount ? crt.settling : ""}` : undefined}
            style={rowStyle}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "baseline",
                    animation: shouldAnimateRow
                        ? `${latestJournalEntryFadeInAnimationName} ${fadeDuration} ${fadeEasing} both`
                        : undefined,
                    animationDelay: shouldAnimateRow
                        ? `${isCyberpunkSubmission ? 0 : fadeInDelayInMilliseconds}ms`
                        : undefined,
                }}
            >
                <span
                    className={isCyberpunk ? `${crt.phosphor} ${crt.timestamp}` : undefined}
                    style={{
                        width: "70px",
                        marginRight: "18px",
                        fontFamily: isCyberpunk ? "inherit" : "monospace",
                        fontVariantNumeric: "tabular-nums",
                        textAlign: "right",
                        opacity: timeStamp && !hideTimeStamp ? 0.75 : 0,
                        color: isCyberpunk ? "inherit" : quickJournalTheme.recentEntry.timestampColor,
                    }}
                >
                    {timeStamp || "0000"}
                </span>
                <span
                    className={isCyberpunk ? crt.phosphor
                        : quickJournalTheme.id === "chromaflare" && wasSubmittedOnMount ? chroma.submissionGlint : undefined}
                    data-afterimage={isCyberpunk && isNewSubmission && typeof text === "string" ? text : undefined}
                >{text}</span>
            </div>
        </div>
    );
}

const recentJournalEntryRowStyle: React.CSSProperties = {
    height: "28px",
    lineHeight: "28px",
    opacity: 0,
};

const latestJournalEntryFadeInAnimationName = "quickJournalLatestEntryFadeIn";

if (typeof document !== "undefined") {
    const styleElementId = "quickJournalLatestEntryFadeStyles";

    if (!document.getElementById(styleElementId)) {
        const styleElement = document.createElement("style");
        styleElement.id = styleElementId;
        styleElement.textContent = `
            @keyframes ${latestJournalEntryFadeInAnimationName} {
                from { opacity: 0; transform: translateY(4px); }
                to { opacity: 1; transform: translateY(0); }
            }
        `;
        document.head.appendChild(styleElement);
    }
}
